<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useThrottleFn } from '@vueuse/core'
import { useRouter, useRoute } from 'vue-router'
import store from '@/store'

import SwordModal from '@/components/modals/SwordModal.vue'
// @ts-expect-error - Module resolution issue with @swordhealth/ui paths
import useActivityTracker from '@swordhealth/ui/composables/activity-tracker'
// @ts-expect-error - Module resolution issue with @swordhealth/ui paths
import useBroadcastTabChannel from '@swordhealth/ui/composables/broadcast-message'
// @ts-expect-error - Module resolution issue with @swordhealth/ui paths
import useTimer from '@swordhealth/ui/composables/timer'

import { AUTH_COOKIE, SESSION_TIMEOUT_CONFIG, CONFIG } from '@/scripts/app-configs/constants'

const router = useRouter()
const route = useRoute()

// Throttle events to prevent emitting from multiple tabs within 3 seconds
const THROTTLE_TIME = 3000
// Countdown time in seconds shown in the modal before auto logout
const MODAL_COUNTDOWN_SECONDS = 60

const resetActivityBroadcastType = 'update:user-activity-reset'
const requireManualActionBroadcastType = 'update:user-manual-action-required'
const userActionBroadcastType = 'update:user-activity-event'

const emit = defineEmits<{
  (e: 'shown', payload: { remainingSeconds: number }): void
  (e: 'snooze', payload: { remainingSeconds: number }): void
  (e: 'expires'): void
}>()

const { onMessage, postMessage } = useBroadcastTabChannel(CONFIG.broadcastChannelName)
const emitShown = useThrottleFn((remainingSeconds: number) => emit('shown', { remainingSeconds }), THROTTLE_TIME)
const emitSnooze = useThrottleFn((remainingSeconds: number) => emit('snooze', { remainingSeconds }), THROTTLE_TIME)
const emitExpires = useThrottleFn(() => emit('expires'), THROTTLE_TIME)

const showTimeoutModal = ref(false)
const fireUserIdled = computed(() => store.getters['user/authentication/isAuthenticated'])

const resetInactivity = (broadcastEv: boolean = true) => {
  if (broadcastEv) postMessage(userActionBroadcastType, resetActivityBroadcastType)
  updateDocumentTitleWithTimer()
  showTimeoutModal.value = false
  if (broadcastEv) emitSnooze(timeRemaining.value)
  resetIdle()
  resetTimer()
}

onMessage(userActionBroadcastType, (message: string) => {
  if (message === requireManualActionBroadcastType) requestManualAction()
  else if (message === resetActivityBroadcastType) resetInactivity(false)
})

const logoutUser = async () => {
  await store.dispatch('user/authentication/logout', { sameUserLoginRoute: route.fullPath })
  await router.replace({ name: 'logout' }).catch(() => {})
}

const onTimerEnds = async () => {
  emitExpires()
  resetInactivity(false)
  await logoutUser()
}

const updateDocumentTitleWithTimer = (currentTimer?: string) => {
  const TIMER_REGEX = /(\[[0-9]{2}:[0-9]{2}\]\s)?(.+)/g

  document.title = document.title.replace(TIMER_REGEX, (curr: string, timer: string, title: string) => {
    return currentTimer ? `[${currentTimer}] ${title}` : title
  })
}

const requestManualAction = () => {
  if (showTimeoutModal.value) return

  restartTimer()
  emitShown(timeRemaining.value)
  updateDocumentTitleWithTimer(stopWatch.value)
  showTimeoutModal.value = true
}

const { restart: restartTimer, reset: resetTimer, stopWatch, timeRemaining } = useTimer(MODAL_COUNTDOWN_SECONDS, onTimerEnds)
const { idle, resetIdle } = useActivityTracker({
  secondsUntilIdle: Number(SESSION_TIMEOUT_CONFIG.autoLogoutTimeout),
  activityCookieName: SESSION_TIMEOUT_CONFIG.sessionTimerStartCookieName,
  cookieDomain: AUTH_COOKIE.token_domain,
  listenForVisibilityChange: true,
})

// On tab boot, inform other tabs about it
postMessage(userActionBroadcastType, resetActivityBroadcastType)

watch(() => idle.value, (val: boolean) => {
  if (!val || !fireUserIdled.value) return
  // Ensure all tabs goes to manual action required state despite clock asynchronisms
  postMessage(userActionBroadcastType, requireManualActionBroadcastType)
  requestManualAction()
})

watch(() => stopWatch.value, (val: string) => {
  if (!showTimeoutModal.value) return
  updateDocumentTitleWithTimer(val)
})
</script>

<template>
  <sword-modal
    v-if="showTimeoutModal"
    type="warning"
    header="copy_4416"
    @close="resetInactivity"
  >
    <template #body>
      <p class="mb-0 max-w-[30rem] p-0">
        {{ $t('copy_4417') }}
      </p>
      <span class="mt-4 block">{{ stopWatch }}</span>
    </template>
    <template #footer>
      <sword-button-wuk theme="primary" text="copy_2" @click.native="resetInactivity" />
    </template>
  </sword-modal>
</template>
